import { useState } from 'react';
import { useLang } from 'hooks/lang';

import { loaders, isOwner } from 'entities';

import { Modal, Button, LabeledSelect, Column } from 'components/Blocks';
import { PropertyWithIcon } from 'components/PropertyWithIcon';

import { CircleDollarIcon, PackagePlusIcon, UserIcon } from 'components/Icons';

import { PackageData } from './Data';

import { useToggleV2, useUser } from 'hooks';
import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';
import { useConfirmation } from 'providers/Confirmation';

import { calculateNewAvailableAmount, canAttachProject } from './utils';
import { formatCurrency } from 'utils/text';
import { cn } from 'styles';

interface Props {
  requestId: string;
  cancel: () => void;
  loading: boolean;
  attachPackageToProject: (packageId: string, quotationId: string) => Promise<boolean>;
}

export function QuotationPackageSelection({
  cancel,
  requestId,
  attachPackageToProject,
  loading: loadingChoice,
}: Props) {
  const { t } = useLang();
  const { project } = useProjectProfile();
  const user = useUser();

  const [selectedPackage, setPackage] = useState('');

  const [loadAllPackages, packageLoadHandlers] = useToggleV2();

  const quotationRequest = project.invoice.quotationRequests.find(({ id }) => id === requestId);

  const { data, loading, error } = loaders.invoice.availablePackages.use({
    blockLoad: !quotationRequest,

    reloadOnChangeRef: loadAllPackages,

    defaultValue: [],

    params: {
      client: quotationRequest?.client,
      currency: quotationRequest?.currency,
    },
  });

  const { requestConfirmation } = useConfirmation();

  const packageData = data.find(({ id }) => id === selectedPackage);

  const close = (): void => {
    cancel();
    packageLoadHandlers.turnOff();
    setPackage('');
  };

  const choosePackage = (): void => {
    requestConfirmation({
      title: t('select-package-name', { name: packageData?.name }) as string,

      type: 'irreversible',

      text: t('package-addition'),

      onConfirm: async () => {
        const success = await attachPackageToProject(selectedPackage, requestId);

        if (!success) return;

        close();
      },
    });
  };

  const availableAmount = calculateNewAvailableAmount(project, packageData, quotationRequest);

  if (!isOwner(project.invoice, user)) return null;

  return (
    <Modal
      isOpen={!!quotationRequest}
      close={close}
      width="min(90vw, 660px)"
      title={t('select-package')}
    >
      <PropertyWithIcon
        icon={<UserIcon />}
        name={t('client')}
        value={quotationRequest?.clientName || ''}
      />

      <PropertyWithIcon
        icon={<CircleDollarIcon />}
        name={t('currency')}
        value={quotationRequest?.currency || ''}
      />

      <Column gap="2">
        <LabeledSelect
          options={data}
          value={selectedPackage}
          label={t('package') || ''}
          loading={loading || error}
          labelKey="name"
          onChangeDirect={setPackage}
        />

        <span className="text-end text-sm italic">{t('only-quoted-packages')}</span>
      </Column>

      {!!packageData && (
        <Column>
          <PackageData data={packageData} />

          <span
            className={cn(
              'font-medium',
              availableAmount >= 0 ? 'text-success' : 'text-destructive',
            )}
          >
            {t('remaining-after-attached')}{' '}
            {formatCurrency(availableAmount.toFixed(2), packageData.currency)}
          </span>
        </Column>
      )}

      <Button
        disabled={!canAttachProject(project, packageData, quotationRequest)}
        onClick={choosePackage}
        loading={loadingChoice}
      >
        <PackagePlusIcon size={16} />
        {t('select')}
      </Button>
    </Modal>
  );
}
